// BankingDetails.js
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, CardHeader, Grid, Paper, Box, Avatar, Typography, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    AccountCircle as AccountCircleIcon,
    AccountBalance as AccountBalanceIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { globalColor } from 'views/ConstantColors';
import { useUsers } from 'hooks/api/users/useUsers';

const BankingDetails = ({ onNext }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { getCurrentUserDemoData } = useUsers();
    const rawDemographicData = getCurrentUserDemoData();

    // Prepare display-safe data
    const demographicData = useMemo(() => {
        if (!rawDemographicData || rawDemographicData.length === 0) return null;

        const data = rawDemographicData[0];

        // Extract department data safely
        let departmentName = 'Not Available';
        let provinceInfo = 'Not Available';

        if (data?.department) {
            if (typeof data.department.projectName === 'string') {
                departmentName = data.department.projectName;
            } else if (typeof data.department.name === 'string') {
                departmentName = data.department.name;
            }

            if (typeof data.department.groupCodeDescription === 'string') {
                provinceInfo = data.department.groupCodeDescription;
            }
        } else if (typeof data?.branchCode === 'string') {
            provinceInfo = data.branchCode;
        }

        return {
            // Personal info
            firstName: data?.user?.firstName || '',
            lastName: data?.user?.lastName || '',
            employeeId: data?.user?.employeeNumber ? String(data.user.employeeNumber) : 'Not Available',
            province: provinceInfo,
            department: departmentName,
            position: data?.user?.employeeTitle ? String(data.user.employeeTitle) : 'Not Available',
            email: data?.user?.email || 'Not Available',

            // Banking info
            hasPaymentDetails: !!data?.user?.paymentDetail,
            bankName: data?.user?.paymentDetail?.bankName || 'Not Available',
            accountName: data?.user?.paymentDetail?.accountName || 'Not Available',
            bsb: data?.user?.paymentDetail?.bsb || 'Not Available',
            accountNumber: data?.user?.paymentDetail?.accountNumber || 'Not Available'
        };
    }, [rawDemographicData]);

    return (
        <Card elevation={3} sx={{ margin: 2 }}>
            <CardHeader
                title={
                    <Typography variant="h5" sx={{ color: globalColor, fontWeight: 'bold' }}>
                        Traveler Banking Details
                    </Typography>
                }
            />
            <CardContent>
                <Grid container spacing={isMobile ? 2 : 4}>
                    {/* Personal Information Section */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ bgcolor: globalColor, width: 56, height: 56, mr: 2 }}>
                                    <AccountCircleIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ color: globalColor }}>
                                        Personal Information
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Employee Details
                                    </Typography>
                                </Box>
                            </Box>
                            {demographicData ? (
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Name
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {`${demographicData.firstName} ${demographicData.lastName}`.trim() || 'Not Available'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Employee ID
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.employeeId}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Province
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.province}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Department
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.department}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Position
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.position}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Email
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.email}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Typography variant="body1">No personal information available</Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Banking Information Section */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ bgcolor: globalColor, width: 56, height: 56, mr: 2 }}>
                                    <AccountBalanceIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ color: globalColor }}>
                                        Banking Information
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Payment Details
                                    </Typography>
                                </Box>
                            </Box>
                            {demographicData && demographicData.hasPaymentDetails ? (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            Bank Name
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.bankName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            Account Name
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.accountName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            BSB
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.bsb}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Account Number
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {demographicData.accountNumber}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Typography variant="body1">No banking information available</Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button variant="contained" color="primary" endIcon={<NavigateNextIcon />} onClick={onNext} size="large">
                        Continue to Acquittal
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

BankingDetails.propTypes = {
    onNext: PropTypes.func.isRequired
};

export default BankingDetails;
